/**
 * Audio playback of melodic patterns and of whole pieces (paper, 7.2: "the addition of audio playback could enhance
 * the user experience"; study feedback P19: no auditory reference). A grand piano: recordings of the Salamander Grand
 * Piano (Yamaha C5, Alexander Holm, CC BY 3.0) sampled every minor third and shifted to the notes between, a
 * damper-like release at the end of each written note and a little room around it. Only the recordings a phrase needs
 * are loaded, once.
 */
import { pianoSamples } from './pianoSamples';

export interface PlayableNote {
  /** MIDI note numbers (several for a chord); empty for a rest. */
  midi: number[];
  /** Onset and duration in quarter notes, relative to the start of the phrase. */
  onset: number;
  duration: number;
}

/** A note of a whole piece (backend api/analysis/performance.py): a tied note is one note. */
export interface ScoreNote {
  midi: number;
  /** Onset and duration in quarter notes from the start of the piece. */
  onset: number;
  duration: number;
  voice: number;
}

interface Stoppable {
  stop: () => void;
  /** Resolves when the phrase or piece has ended or was stopped. */
  finished: Promise<void>;
}

/** A phrase or piece that plays: it can be stopped and followed (scoreFollowing.ts). */
export interface Playback extends Stoppable {
  /** Resolves when the piano is loaded and the music starts to sound (or when it was stopped before). */
  started: Promise<void>;
  /**
   * Where the music is, in quarter notes: from the start of a phrase, or from the start of the piece for a whole piece
   * (where it starts while the piano loads).
   */
  position: () => number;
}

const letterSemitones: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** MIDI number of a music21 pitch name such as 'E-4', 'F#5' or 'C##3' ('-' is a flat). */
export const midiFromName = (name: string): number | undefined => {
  const match = /^([A-G])(#{1,2}|-{1,2})?(-?\d+)$/.exec(name);
  if (!match) return undefined;
  const [, letter, accidental = '', octave] = match;
  const alteration = accidental.startsWith('#') ? accidental.length : -accidental.length;

  return (Number(octave) + 1) * 12 + letterSemitones[letter] + alteration;
};

const sampledNotes = Object.keys(pianoSamples).map(Number);

/** The recording closest to the note; it is at most one and a half semitones away. */
const nearestSample = (midi: number) =>
  sampledNotes.reduce((best, sampled) =>
    Math.abs(sampled - midi) < Math.abs(best - midi) ? sampled : best
  );

// Time constant of the release when a key is let go (the dampers stop the strings within about half a second).
const release = 0.12;
// How long a released note may ring before its source is stopped.
const releaseTail = 1;
// A whole piece is scheduled this many seconds ahead, every `schedulingInterval` milliseconds: Web Audio keeps the
// time, the main thread only has to keep up (a tab that plays audio is not throttled).
const lookahead = 1.5;
const schedulingInterval = 200;

let context: AudioContext | undefined;
let output: AudioNode | undefined;
let current: Stoppable | undefined;
const recordings = new Map<number, Promise<AudioBuffer>>();

const recording = (audio: AudioContext, sampled: number) => {
  if (!recordings.has(sampled)) {
    const loaded = fetch(pianoSamples[sampled])
      .then((response) => response.arrayBuffer())
      .then((data) => audio.decodeAudioData(data));
    // A failed download may be retried with the next phrase.
    loaded.catch(() => recordings.delete(sampled));
    recordings.set(sampled, loaded);
  }

  return recordings.get(sampled);
};

/** The recordings for the MIDI notes, by sampled note. */
const recordingsFor = (audio: AudioContext, midi: number[]) =>
  Promise.all(
    [...new Set(midi.map(nearestSample))].map((sampled) =>
      recording(audio, sampled).then((buffer) => [sampled, buffer] as const)
    )
  ).then((loaded) => new Map(loaded));

/** A short, diffuse room: decaying noise as impulse response, a little later on the right than on the left. */
const room = (audio: AudioContext) => {
  const seconds = 1.6;
  const impulse = audio.createBuffer(2, Math.round(audio.sampleRate * seconds), audio.sampleRate);
  for (let channel = 0; channel < 2; channel += 1) {
    const samples = impulse.getChannelData(channel);
    const delay = Math.round(audio.sampleRate * (0.012 + 0.006 * channel));
    for (let index = delay; index < samples.length; index += 1) {
      const time = (index - delay) / audio.sampleRate;
      samples[index] = (Math.random() * 2 - 1) * Math.exp(-time / 0.35);
    }
  }
  const reverb = audio.createConvolver();
  reverb.buffer = impulse;

  return reverb;
};

/** Piano bus: dry signal and some room, through a limiter that only catches the peaks of chords. */
const outputOf = (audio: AudioContext) => {
  if (output) return output;

  const input = audio.createGain();
  // A hard knee high up: single notes keep their dynamics (the default soft knee of 30 dB would squash them).
  const limiter = audio.createDynamicsCompressor();
  limiter.threshold.value = -3;
  limiter.knee.value = 3;
  limiter.ratio.value = 12;
  limiter.attack.value = 0.002;
  limiter.release.value = 0.2;
  const dry = audio.createGain();
  dry.gain.value = 0.9;
  const wet = audio.createGain();
  wet.gain.value = 0.18;
  const reverb = room(audio);

  input.connect(dry).connect(limiter);
  input.connect(reverb).connect(wet).connect(limiter);
  limiter.connect(audio.destination);
  output = input;

  return output;
};

interface Session {
  audio: AudioContext;
  playback: Stoppable;
  isStopped: () => boolean;
  /** Runs when the session stops, e.g. to clear the caller's timers. */
  onStop: (cleanup: () => void) => void;
  /** One key: the nearest recording shifted to the note, held from `start` to `end` and released like a damper. */
  strike: (
    buffers: Map<number, AudioBuffer>,
    midi: number,
    start: number,
    end: number,
    velocity: number
  ) => void;
}

/** A playback on a bus of its own; it stops the previous one. Stopping fades the bus out and ends its sources. */
const startSession = (): Session => {
  current?.stop();
  context = context ?? new AudioContext();
  const audio = context;
  // Browsers create the context suspended until a user gesture; playing is always started by a click.
  void audio.resume();

  const bus = audio.createGain();
  bus.connect(outputOf(audio));
  const sources = new Set<AudioBufferSourceNode>();
  const cleanups: (() => void)[] = [];
  let isStopped = false;
  let resolveFinished: () => void = () => undefined;
  const finished = new Promise<void>((resolve) => (resolveFinished = resolve));

  const stop = () => {
    if (isStopped) return;
    isStopped = true;
    cleanups.forEach((cleanup) => cleanup());
    bus.gain.cancelScheduledValues(audio.currentTime);
    bus.gain.setTargetAtTime(0, audio.currentTime, 0.04);
    window.setTimeout(() => {
      sources.forEach((source) => source.stop());
      bus.disconnect();
    }, 400);
    if (current === playback) current = undefined;
    resolveFinished();
  };
  const playback: Stoppable = { finished, stop };
  current = playback;

  const strike: Session['strike'] = (buffers, midi, start, end, velocity) => {
    const sampled = nearestSample(midi);
    const source = audio.createBufferSource();
    source.buffer = buffers.get(sampled) ?? null;
    source.playbackRate.value = 2 ** ((midi - sampled) / 12);
    const key = audio.createGain();
    key.gain.setValueAtTime(velocity, start);
    key.gain.setValueAtTime(velocity, end);
    key.gain.setTargetAtTime(0, end, release);
    source.connect(key).connect(bus);
    source.start(start);
    source.stop(end + releaseTail);
    sources.add(source);
    // A piece strikes thousands of keys: each leaves the graph when it has rung out.
    source.onended = () => {
      sources.delete(source);
      key.disconnect();
    };
  };

  return {
    audio,
    isStopped: () => isStopped,
    onStop: (cleanup) => cleanups.push(cleanup),
    playback,
    strike
  };
};

/** Plays the notes at `bpm` quarter notes per minute; a new phrase stops the previous one. */
export const play = (notes: PlayableNote[], bpm = 80): Playback => {
  const session = startSession();
  const { audio, playback } = session;
  const secondsPerQuarter = 60 / bpm;
  let start: number | undefined;
  let timer: number | undefined;
  session.onStop(() => window.clearTimeout(timer));
  let resolveStarted: () => void = () => undefined;
  const started = new Promise<void>((resolve) => (resolveStarted = resolve));
  playback.finished.then(resolveStarted);

  recordingsFor(
    audio,
    notes.flatMap((note) => note.midi)
  )
    .then((buffers) => {
      if (session.isStopped()) return;

      const startTime = audio.currentTime + 0.06;
      start = startTime;
      let end = startTime;
      notes.forEach((note) => {
        const noteStart = startTime + note.onset * secondsPerQuarter;
        const noteEnd = noteStart + Math.max(note.duration * secondsPerQuarter, 0.08);
        // Chords keep the loudness of a single note.
        const velocity = 0.75 / Math.sqrt(Math.max(note.midi.length, 1));
        note.midi.forEach((midi) => session.strike(buffers, midi, noteStart, noteEnd, velocity));
        end = Math.max(end, noteEnd + 5 * release);
      });
      timer = window.setTimeout(playback.stop, (end - audio.currentTime) * 1000);
      resolveStarted();
    })
    .catch(playback.stop);

  return {
    ...playback,
    position: () =>
      start === undefined ? 0 : Math.max(0, audio.currentTime - start) / secondsPerQuarter,
    started
  };
};

/**
 * Plays a whole piece from `from` (quarter notes) at `bpm` quarter notes per minute. The recordings it needs are
 * loaded first; then its notes are scheduled a little ahead while it plays, so a long piece starts at once and stops
 * at once. A new phrase or piece stops it.
 */
export const playScore = (notes: ScoreNote[], bpm: number, from = 0): Playback => {
  const session = startSession();
  const { audio, playback } = session;
  const secondsPerQuarter = 60 / bpm;
  const remaining = notes.filter((note) => note.onset >= from - 1e-6);
  const end = remaining.reduce(
    (latest, note) => Math.max(latest, note.onset + note.duration),
    from
  );
  // Notes struck together share the loudness of one, like a chord: the voices entering do not add up.
  const together = new Map<number, number>();
  remaining.forEach((note) => together.set(note.onset, (together.get(note.onset) ?? 0) + 1));

  let start: number | undefined;
  let next = 0;
  let interval: number | undefined;
  session.onStop(() => window.clearInterval(interval));
  let resolveStarted: () => void = () => undefined;
  const started = new Promise<void>((resolve) => (resolveStarted = resolve));
  playback.finished.then(resolveStarted);

  const schedule = (buffers: Map<number, AudioBuffer>, startTime: number) => {
    const horizon = audio.currentTime + lookahead;
    while (next < remaining.length) {
      const note = remaining[next];
      const noteStart = startTime + (note.onset - from) * secondsPerQuarter;
      if (noteStart > horizon) break;
      const noteEnd = noteStart + Math.max(note.duration * secondsPerQuarter, 0.08);
      session.strike(
        buffers,
        note.midi,
        noteStart,
        noteEnd,
        0.6 / Math.sqrt(together.get(note.onset))
      );
      next += 1;
    }
    if (audio.currentTime > startTime + (end - from) * secondsPerQuarter + 5 * release)
      playback.stop();
  };

  recordingsFor(
    audio,
    remaining.map((note) => note.midi)
  )
    .then((buffers) => {
      if (session.isStopped()) return;

      const startTime = audio.currentTime + 0.1;
      start = startTime;
      schedule(buffers, startTime);
      interval = window.setInterval(() => schedule(buffers, startTime), schedulingInterval);
      resolveStarted();
    })
    .catch(playback.stop);

  return {
    ...playback,
    position: () =>
      start === undefined
        ? from
        : from + Math.max(0, audio.currentTime - start) / secondsPerQuarter,
    started
  };
};

export const stopPlayback = () => current?.stop();
